import { useTracking } from 'react-tracking';

import { Heading, Text, Grid, useTheme } from '@simplywallst/ui-core';

import type { RiskRewardStatements } from '../../hooks/useCompanyRisksRewards';

import Tooltip from '@/components/Tooltip';
import { RiskRewardStatement } from '@/components/RiskReward';

import { TooltipTextWrapper, InformationIcon } from './styled';

interface Props {
  statements: RiskRewardStatements;
  tickerSymbol: string;
}

const getFormattedDescription = (description: string) =>
  description.replace(/\[\[(.*?)\]\]/g, (_matched, original) => {
    return original;
  });

export const RisksRewards = ({ statements, tickerSymbol }: Props) => {
  const theme = useTheme();

  const { Track } = useTracking({
    type: 'track',
    subject: 'RisksRewards',
  });

  const hasRewards = statements.rewards.length > 0;
  const hasRisks = statements.risks.length > 0;

  return (
    <Track>
      <Grid
        templateColumns="repeat(2, max-content)"
        justifyContent="space-between"
        alignItems="center"
      >
        <Heading as="h4" typography="tiny" color="whiteRGBA" opacity={0.5}>
          {hasRewards ? 'Rewards' : 'Risk Analysis'}
        </Heading>
        <Tooltip
          title={
            <TooltipTextWrapper>
              <Text textAlign="center" typography="tiny" color="spaceGray">
                Our platform conducts automated risk assessments for each
                company and highlights any failed checks as potential investment
                risks. The risk-reward analysis provides a summary of the pros
                and cons for each company. Note that a passing result on all
                checks does not guarantee a 'risk-free' investment.
              </Text>
            </TooltipTextWrapper>
          }
          light
          placement="top"
          zIndex={99999}
        >
          <Grid>
            <InformationIcon fill={theme.color.whiteRGBA(0.5)} />
          </Grid>
        </Tooltip>
      </Grid>
      {hasRewards ? (
        <Grid templateRows="auto">
          {statements.rewards.map((reward) => {
            return (
              <RiskRewardStatement
                typography="tiny"
                key={reward.name}
                severity={reward.severity}
                description={getFormattedDescription(
                  reward.description.description
                )}
              />
            );
          })}
        </Grid>
      ) : null}
      {hasRewards ? (
        <Heading as="h4" typography="tiny" color="whiteRGBA" opacity={0.5}>
          Risk Analysis
        </Heading>
      ) : null}
      {hasRisks ? (
        <Grid templateRows="auto">
          {statements.risks.map((risk) => {
            return (
              <RiskRewardStatement
                typography="tiny"
                key={risk.name}
                severity={risk.severity}
                description={getFormattedDescription(
                  risk.description.description
                )}
              />
            );
          })}
        </Grid>
      ) : (
        <Text typography="tiny">
          No risks detected for {tickerSymbol} from our risks checks.
        </Text>
      )}
    </Track>
  );
};
